import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable, tap} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {Additionals} from "../item/additionals";
import {UrlBuilder} from "../item/url.item";
import {map} from "rxjs/operators";
import {VoidDto} from "../dto/void.dto";
import {ReceiptDto} from "../dto/receipt.dto";
import {SearchReceiptRes} from "../dto/search/search-receipt.res";
import {SearchBookingCategoryRes} from "../dto/search/search-booking-category.res";
import {ReceiptPositionDto} from "../dto/receipt-position.dto";
import {CheckDto} from "../dto/check.dto";
import {DocumentCommentDto} from "../dto/document-comment.dto";
import {SearchCommentRes} from "../dto/search/search-comment.res";

@Injectable({providedIn: 'root'})
export class ReceiptRepo {

  private readonly receiptRead = '/v1/org/{orgId}/receipt/{id}';
  private readonly receiptWrite = '/v1/org/{orgId}/receipt';
  private readonly receiptDelete = '/v1/org/{orgId}/receipt/{id}/delete';
  private readonly receiptReviewed = '/v1/org/{orgId}/receipt/{id}/reviewed';
  private readonly receiptUpload = '/v1/org/{orgId}/receipt/{id}/type/{type}/upload';
  private readonly receiptCheckDuplicates = '/v1/org/{orgId}/receipt/{id}/check/duplicates';
  private readonly receiptCommentWrite = '/v1/org/{orgId}/receipt/{id}/comment';
  private readonly receiptCommentSearch = '/v1/org/{orgId}/receipt/comment/search';
  private readonly receiptPositionsWrite = '/v1/org/{orgId}/receipt/{id}/position/all';
  private readonly receiptPosWrite = '/v1/org/{orgId}/receipt/{id}/position';
  private readonly receiptPosDelete = '/v1/org/{orgId}/receipt/{id}/position/{posId}/delete';
  private readonly receiptSearch = '/v1/org/{orgId}/receipt/search';
  private readonly receiptCategorySearch = '/v1/org/{orgId}/receipt/category/search';

  private readonly http: HttpClient = inject(HttpClient);

  public getById(org: OrgDto, receiptId: string, add?: Additionals): Observable<WrapperDto<ReceiptDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptRead).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', receiptId).build();
    return this.http.get<WrapperDto<ReceiptDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public update(org: OrgDto, receipt: ReceiptDto, add?: Additionals): Observable<WrapperDto<ReceiptDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptWrite).addParam('orgId', org.id).addAdditionals(add).build();
    return this.http.post<WrapperDto<ReceiptDto>>(url.getUrl(), receipt, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public delete(org: OrgDto, receiptId: string): Observable<WrapperDto<VoidDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptDelete)
      .addParam('orgId', org.id).addParam('id', receiptId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public reviewed(org: OrgDto, receiptId: string, add?: Additionals): Observable<WrapperDto<ReceiptDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptReviewed).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', receiptId).build();
    return this.http.post<WrapperDto<ReceiptDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public upload(org: OrgDto, receiptId: string | null, type: string, file: File): Observable<WrapperDto<ReceiptDto>> {
    const data = new FormData();
    data.append('media', file);
    const url = UrlBuilder.builder().addPath(this.receiptUpload)
      .addParam('orgId', org.id).addParam('id', receiptId).addParam('type', type)
      .setContentTypeMultipartFormData().build();
    return this.http.post<WrapperDto<ReceiptDto>>(url.getUrl(), data, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public hasDuplicates(org: OrgDto, receiptId: string, receiptNumber: string, contactId: string): Observable<WrapperDto<CheckDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptCheckDuplicates)
      .addParam('orgId', org.id).addParam('id', receiptId)
      .addQueryParams('receiptNumber', receiptNumber).addQueryParams('contactId', contactId).build();
    return this.http.get<WrapperDto<CheckDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Comment
  // ##########################################################################

  // noinspection JSUnusedGlobalSymbols
  public updateComment(org: OrgDto, receiptId: string, comment: DocumentCommentDto): Observable<WrapperDto<DocumentCommentDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptCommentWrite)
      .addParam('orgId', org.id).addParam('id', receiptId).build();
    return this.http.post<WrapperDto<DocumentCommentDto>>(url.getUrl(), comment, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public searchComment(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchCommentRes>> {
    const url = UrlBuilder.builder().addPath(this.receiptCommentSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchCommentRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchCommentRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  // ##########################################################################
  // #### Positions
  // ##########################################################################

  public updateAllPositions(org: OrgDto, receiptId: string, positions: ReceiptPositionDto[], add?: Additionals): Observable<WrapperDto<ReceiptDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptPositionsWrite).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', receiptId).build();
    return this.http.post<WrapperDto<ReceiptDto>>(url.getUrl(), positions, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updatePosition(org: OrgDto, receiptId: string, position: ReceiptPositionDto): Observable<WrapperDto<ReceiptPositionDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptPosWrite)
      .addParam('orgId', org.id).addParam('id', receiptId).build();
    return this.http.post<WrapperDto<ReceiptPositionDto>>(url.getUrl(), position, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deletePosition(org: OrgDto, receiptId: string, posId: string): Observable<WrapperDto<ReceiptPositionDto>> {
    const url = UrlBuilder.builder().addPath(this.receiptPosDelete)
      .addParam('orgId', org.id).addParam('id', receiptId).addParam('posId', posId).build();
    return this.http.post<WrapperDto<ReceiptPositionDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchReceiptRes>> {
    const url = UrlBuilder.builder().addPath(this.receiptSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchReceiptRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchReceiptRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  public bookingCategorySearch(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchBookingCategoryRes>> {
    const url = UrlBuilder.builder().addPath(this.receiptCategorySearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchBookingCategoryRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchBookingCategoryRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }
}
